import { motion } from "framer-motion";

const chapters = [
  {
    n: "01",
    kicker: "Trust",
    title: "A driver on your team, not a stranger at the door.",
    body: "Every Waylio driver clears a background check and drug screen before their first route. For medical and pharmaceutical runs, that includes HIPAA and OSHA certification — because who's handling your package matters as much as when it arrives.",
    img: "/about-trust.jpg",
    imgAlt: "Line illustration of a friendly courier driver giving a thumbs-up beside a trust badge",
  },
  {
    n: "02",
    kicker: "Partnership",
    title: "Built for the businesses that come back.",
    body: "The best courier is the one you don't have to think about. Set us up as your standing pickup — same time each week, the same driver, the same standards — and Waylio quietly becomes an extension of your team. Most of our clients started with one delivery and have been with us every week since.",
    img: "/about-partnership.jpg",
    imgAlt: "Line illustration of a shop storefront and delivery van linked in a recurring partnership loop",
  },
  {
    n: "03",
    kicker: "Proof",
    title: "Nothing left to wonder about, ever.",
    body: "Photo, signature, or receipt — proof of delivery lands in your inbox or on your phone the moment the package changes hands. No dashboards to log into, no chasing status updates.",
    img: "/about-proof.jpg",
    imgAlt: "Line illustration of proof of delivery arriving as a phone notification beside a parcel",
  },
];

export default function Manifesto() {
  return (
    <section
      id="about"
      className="bg-forest text-cream py-24 md:py-32 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-10 -right-10 font-serif italic text-[18vw] leading-none text-cream/5 select-none pointer-events-none"
      >
        about us
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-8">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">
              About Waylio
            </div>
            <h2 className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              We're built to make a{" "}
              <span className="text-terracotta">way</span> to keep your business{" "}
              <span className="text-terracotta">moving.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-4 flex items-end">
            <p className="text-base md:text-lg text-cream/70 leading-relaxed">
              From medical supplies to important documents, Waylio makes
              getting it there simple. We deliver with speed, care, and
              precision—so you can focus on what matters most.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-10">
          {chapters.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`col-span-12 md:col-span-4 border-t border-cream/20 pt-6 ${
                i === 1 ? "md:mt-16" : i === 2 ? "md:mt-8" : ""
              }`}
            >
              <img
                src={c.img}
                alt={c.imgAlt}
                data-testid={`about-illustration-${c.n}`}
                className="mb-8 w-40 h-40 md:w-56 md:h-56"
                loading="lazy"
              />
              <div className="flex items-baseline gap-4">
                <span className="chapter-num text-6xl text-terracotta">{c.n}</span>
                <span className="text-xs uppercase tracking-[0.3em] text-cream/70">
                  {c.kicker}
                </span>
              </div>
              <h3 className="mt-6 font-serif text-2xl md:text-3xl leading-[1.15] text-cream">
                {c.title}
              </h3>
              <p className="mt-4 text-cream/70 leading-relaxed text-sm md:text-base">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
